import { Component, OnInit, Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalService } from '../services/local.service';
import { NgForm } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SwUpdate } from '@angular/service-worker';
import { ActivatedRoute } from '@angular/router';
import { APIResponse } from '../interfaces/response';
import { PuzzleService } from '../services/puzzle.service';
import { environment } from 'src/environments/environment';
import { ResponseSmallDialogComponent } from '../dialogs/response-small-dialog/response-small-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-candidate-quest',
  templateUrl: './candidate-quest.component.html',
  styleUrls: ['./candidate-quest.component.css'],
})
export class CandidateQuestComponent implements OnInit {
  currentLang: string;
  companyNameNormal: string;
  candidateName: string;
  candidateId: string;
  questionaire: any;
  isTokenValid: boolean = null;
  backupID: string;
  isFormAlreadySubmitted: boolean = false;
  sending: boolean = false;
  isWizardContainerExpandedQuestionnaire: boolean = true;
  tokenParam: string;

  logoImage: SafeResourceUrl = '../../assets/icons/haibu-icon.svg';
  haibuBroken: SafeResourceUrl = '../../assets/animations/haibu-broken.gif';
  haibuCheck: SafeResourceUrl = '../../assets/animations/haibu-complete.gif';
  haibuLoading: SafeResourceUrl = '../../assets/animations/haibu-loading.gif';

  constructor(
    private translate: TranslateService,
    private localService: LocalService,
    private swUpdate: SwUpdate,
    private activatedRoute: ActivatedRoute,
    private puzzleService: PuzzleService,
    private sanitizer: DomSanitizer,
    private renderer: Renderer2,
    public dialog: MatDialog,
  ) {
    this.swUpdate.checkForUpdate();
    try {
      this.tokenParam = this.activatedRoute.snapshot.queryParamMap.get('id');
      this.validateToken(this.tokenParam);
    } catch (error) {
      console.error(error);
    }
  }

  ngOnInit(): void {
    this.initializeLanguage();
  }

  validateToken(token: string) {
    this.puzzleService.validateCfToken(token).subscribe(
      (response: APIResponse) => {
        if (response.isSuccess) {
          this.isTokenValid = true;
          if (response.data.questionnaires?.length > 0) {
            this.questionaire = response.data.questionnaires[0];
          } else {
            this.isTokenValid = false;
          }
          this.companyNameNormal = response.data.companyName;
          if (response.data.companyLogo) {
            this.logoImage = this.sanitizer.bypassSecurityTrustResourceUrl(
              `${environment.imageUrl}${response.data.companyLogo}`,
            );
          }
          this.candidateId = response.data.candidateId;
          this.candidateName = response.data.candidateName;
          this.backupID = response.data.backupId;
        } else {
          this.isTokenValid = false;
        }
      },
      (error) => {
        console.error('An error occurred while validating token', error);
        this.isTokenValid = false;
      },
    );
  }

  changeLanguage(lang: string): void {
    const newLang = lang;
    this.translate.use(newLang);
    this.currentLang = newLang;
    this.localService.setJsonValue('lang', newLang);
  }

  initializeLanguage() {
    if (this.localService.getJsonValue('lang')) {
      this.translate.use(this.localService.getJsonValue('lang'));
      this.currentLang = this.localService.getJsonValue('lang');
    } else {
      const browserLang = this.translate.getBrowserLang();
      let appLang = this.mapBrowserLangToAppLang(browserLang);
      this.translate.use(appLang);
      this.currentLang = appLang;
      this.localService.setJsonValue('lang', appLang);
    }
  }

  mapBrowserLangToAppLang(browserLang: string): string {
    const langMap = {
      en: 'eng',
      es: 'esp',
    };
    return langMap[browserLang] || 'eng';
  }

  toggleWizardContainerQuestionnaire() {
    this.isWizardContainerExpandedQuestionnaire =
      !this.isWizardContainerExpandedQuestionnaire;
  }

  onFocus(event: FocusEvent) {
    const target = event.target as HTMLElement;
    this.renderer.addClass(target, 'focus-input');
  }

  onBlur(event: FocusEvent) {
    const target = event.target as HTMLElement;
    this.renderer.removeClass(target, 'focus-input');
  }

  onSelectionChange(event: any, question: any) {
    if (event.source.selected.length > question.correctOptionCount) {
      event.source.deselect(event.option.value);
    }
  }

  isOptionDisabled(question: any, optionId: any): boolean {
    return (
      question.response &&
      question.response.length >= question.correctOptionCount &&
      !question.response.includes(optionId)
    );
  }

  validateTokenAnswered(token: string, callback: () => void) {
    this.puzzleService.validateCfTokenAnswered(token).subscribe((response) => {
      if (response.isSuccess) {
        this.isTokenValid = true;
      } else {
        this.isTokenValid = false;
      }
      callback();
    });
  }

  async sendQuestionnaire(myForm: NgForm) {
    if (this.sending) return;

    const questionnaireInvalid = !this.checkIfAllQuestionnaireFilled();

    if (questionnaireInvalid) {
      this.openResultDialog(
        0,
        this.translate.instant('candidateCfErrorIncomplete'),
      );
      return;
    }

    this.sending = true;
    this.validateTokenAnswered(this.tokenParam, () => {
      if (!this.isTokenValid || this.isFormAlreadySubmitted) {
        this.sending = false;
        return;
      }

      //subir respuestas de cuestionario
      if (this.questionaire) {
        const responses = {
          CANDIDATE_ID: this.candidateId,
          BACKUP_ID: this.backupID,
          RESPONSES: this.questionaire.questions.map((question) => {
            if (question.questioN_TYPE == 'OPEN_ENDED') {
              return {
                QUESTION_ID: question.questioN_ID,
                ANSWER_TEXT: question.response,
              };
            } else if (
              question.questioN_TYPE == 'YES_NO' ||
              question.questioN_TYPE == 'MULTIPLE_CHOICE'
            ) {
              return {
                QUESTION_ID: question.questioN_ID,
                SELECTED_OPTION_IDS: [question.response],
              };
            } else if (question.questioN_TYPE == 'CHECKBOXES') {
              return {
                QUESTION_ID: question.questioN_ID,
                SELECTED_OPTION_IDS: question.response,
              };
            }
          }),
        };
        this.puzzleService.saveCandidateResponses(responses).subscribe(
          (response: APIResponse) => {
            if (response.isSuccess) {
              this.isFormAlreadySubmitted = true;
              this.puzzleService.markCFAsUsed(this.tokenParam).toPromise();
            } else {
              this.sending = false;
              this.openResultDialog(
                0,
                'Error',
                this.translate.instant('candidateCfError'),
              );
            }
          },
          (error) => {
            console.error(
              'An error occurred while sending questionnaire',
              error,
            );
            this.sending = false;
            this.openResultDialog(
              0,
              'Error',
              this.translate.instant('candidateCfError'),
            );
          },
        );
      }
    });
  }

  openResultDialog(mode: number, message?: string, content?: string) {
    let data: string[] = [];
    switch (mode) {
      case 0:
        {
          data.push('Error');
          data.push(message);
          if (content) data.push(content);
          return this.dialog.open(ResponseSmallDialogComponent, {
            width: '500px',
            data: data,
          });
        }
        break;
      case 1:
        {
          data.push('Exito');
          data.push(message);
          if (content) data.push(content);

          return this.dialog.open(ResponseSmallDialogComponent, {
            width: '500px',
            data: data,
          });
        }
        break;
      case 2:
        {
          data.push('loading');
          data.push(message);
          if (content) data.push(content);

          return this.dialog.open(ResponseSmallDialogComponent, {
            width: '500px',
            data: data,
            disableClose: true,
          });
        }
        break;
    }
  }

  checkIfAllQuestionnaireFilled() {
    if (this.questionaire && this.questionaire.questions) {
      let error = false;
      for (let i = 0; i < this.questionaire.questions.length; i++) {
        if (typeof this.questionaire.questions[i].response === 'string') {
          this.questionaire.questions[i].response =
            this.questionaire.questions[i].response.trim();
        }
        if (
          !this.questionaire.questions[i].response ||
          this.questionaire.questions[i].response == ''
        ) {
          this.questionaire.questions[i].invalid = true;
          error = true;
        } else {
          this.questionaire.questions[i].invalid = false;
        }
      }
      if (error) {
        return false;
      }
    }
    return true;
  }
}
